import { makeObservable, observable } from "mobx"

import { LcAddress } from "../core"
import { ClFarmStore } from "./cl-farm-store"

export class ClFarmRepository {
  farms = new Map<string, ClFarmStore>()

  constructor() {
    makeObservable(this, {
      farms: observable,
    })
  }

  // Add farm to repository
  add(farm: ClFarmStore): void {
    const existing = this.find(farm.address)
    if (existing) {
      return
    }

    this.farms.set(farm.address.toString(), farm)
  }

  // Find farm by linked pool address, i.e. minimal pool  etc
  find(address: LcAddress): ClFarmStore | undefined {
    return this.farms.get(address.toString())
  }

  // Find farm by gauge address
  findByGauge(gaugeAddress: LcAddress): ClFarmStore | undefined {
    const allFarms = Array.from(this.farms.values())
    const matchingFarms = allFarms.filter((farm) => farm.gaugeAddress?.equals(gaugeAddress))

    // Only log if we expected to find a farm but didn't
    // (This will be logged by the caller if needed)
    return matchingFarms[0]
  }

  getAll(): ClFarmStore[] {
    return Array.from(this.farms.values())
  }

  get totalFarms(): number {
    return this.farms.size
  }

  get totalTvlUsd(): number {
    return Array.from(this.farms.values()).reduce((acc, farm) => acc + farm.tvlUsd, 0)
  }

  // Get all farms with gauge positions or rewards
  // Shows farms if user has a staked position OR has rewards to claim
  getAllWithGaugeRewards(): ClFarmStore[] {
    return this.getAll().filter((farm) => {
      if (!farm.gaugeAddress) {
        return false
      }

      // Show if user has a staked position (gauge balance or reserves > 0)
      const hasStakedPosition =
        farm.onChainUserGaugeBalance > 0n || farm.onChainUserGaugeReserve0 > 0n || farm.onChainUserGaugeReserve1 > 0n
      // Show if user has rewards to claim
      const hasRewards = farm.onChainUserGaugeGovernanceEarned > 0n
      return hasStakedPosition || hasRewards
    })
  }

  // Clear user-specific values from all farms
  clearAllUserValues(): void {
    this.farms.forEach((farm) => {
      // Reset user-specific on-chain values to defaults
      farm.onChainUserBalance = 0n
      farm.onChainUserBalanceToken0 = 0n
      farm.onChainUserBalanceToken1 = 0n
      farm.onChainUserClaimableFees0 = 0n
      farm.onChainUserClaimableFees1 = 0n
      farm.onChainUserGaugeBalance = 0n
      farm.onChainUserGaugeGovernanceEarned = 0n
      farm.onChainUserGaugeReserve0 = 0n
      farm.onChainUserGaugeReserve1 = 0n
    })
  }
}
